import { toast } from 'react-hot-toast';

export interface LocationData {
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  timezone?: string;
  accuracy?: number;
}

export interface IPLocationData {
  ip: string;
  city: string;
  region: string;
  country: string;
  country_code: string;
  timezone: string;
  latitude: number;
  longitude: number;
}

/**
 * Enhanced location service with permission dialog and multiple location sources
 */
export class LocationService {
  private static instance: LocationService;
  
  static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  /**
   * Get user's location with permission dialog
   */
  async getUserLocation(showPermissionDialog: boolean = true): Promise<LocationData | null> {
    try {
      // First try to get precise GPS location
      const gpsLocation = await this.getGPSLocation(showPermissionDialog);
      if (gpsLocation) {
        return gpsLocation;
      }

      // Fallback to IP-based location
      const ipLocation = await this.getIPLocation();
      if (ipLocation) {
        return ipLocation;
      }

      return null;
    } catch (error) {
      console.error('Failed to get user location:', error);
      return null;
    }
  }

  /**
   * Get GPS location with permission handling
   */
  private async getGPSLocation(showDialog: boolean): Promise<LocationData | null> {
    if (!navigator.geolocation) {
      if (showDialog) {
        toast.error('Geolocation is not supported by this browser');
      }
      return null;
    }

    // Check current permission status
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        
        if (permission.state === 'denied') {
          if (showDialog) {
            toast.error('Location access denied. Please enable in browser settings.');
          }
          return null;
        }
      } catch (error) {
        console.warn('Could not check geolocation permission:', error);
      }
    }

    return new Promise((resolve) => {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          try {
            // Reverse geocode to get address
            const addressData = await this.reverseGeocode(latitude, longitude);
            
            resolve({
              latitude,
              longitude,
              accuracy,
              ...addressData
            });
          } catch (error) {
            console.warn('Reverse geocoding failed:', error);
            resolve({
              latitude,
              longitude,
              accuracy
            });
          }
        },
        (error) => {
          if (showDialog) {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                toast.error('Location access denied by user');
                break;
              case error.POSITION_UNAVAILABLE:
                toast.error('Location information unavailable');
                break;
              case error.TIMEOUT:
                toast.error('Location request timed out');
                break;
              default:
                toast.error('An unknown error occurred while retrieving location');
                break;
            }
          }
          resolve(null);
        },
        options
      );
    });
  }

  /**
   * Get IP-based location using ipapi.co (free tier)
   */
  private async getIPLocation(): Promise<LocationData | null> {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error('IP location service unavailable');
      }

      const data: IPLocationData = await response.json();
      
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.city,
        state: data.region,
        country: data.country,
        countryCode: data.country_code,
        timezone: data.timezone
      };
    } catch (error) {
      console.warn('IP location lookup failed:', error);
      return null;
    }
  }

  /**
   * Reverse geocode coordinates to address using OpenStreetMap Nominatim
   */
  private async reverseGeocode(lat: number, lon: number): Promise<Partial<LocationData>> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Reverse geocoding failed');
      }

      const data = await response.json();
      const address = data.address || {};

      return {
        city: address.city || address.town || address.village || address.hamlet,
        state: address.state || address.region,
        country: address.country,
        countryCode: address.country_code?.toUpperCase()
      };
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      return {};
    }
  }

  /**
   * Request location permission with custom dialog
   */
  async requestLocationPermission(): Promise<boolean> {
    if (!navigator.geolocation) {
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { timeout: 5000 }
      );
    });
  }

  /**
   * Check if geolocation is supported
   */
  isGeolocationSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Format location for display
   */
  formatLocation(location: LocationData): string {
    const parts = [];
    
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.country) parts.push(location.country);
    
    return parts.join(', ') || 'Unknown Location';
  }
}

export const locationService = LocationService.getInstance();
